// Create a single AudioContext to be reused.
// It's best practice to not create a new one for each sound.
let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
    if (typeof window !== 'undefined' && !audioContext) {
        try {
            // Safari requires the webkit prefix
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            if (AudioContext) {
                audioContext = new AudioContext();
            }
        } catch (e) {
            console.error("Web Audio API is not supported in this browser.", e);
            return null;
        }
    }
    return audioContext;
};

// Function to play a tone
const playTone = (frequency: number, duration: number, volume: number) => {
    const context = getAudioContext();
    if (!context) return;
    if (context.state === 'suspended') {
        context.resume().catch(console.error);
    }
    
    // Use a GainNode to control the volume and prevent clicking sounds
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, context.currentTime);

    gainNode.gain.setValueAtTime(0.001, context.currentTime); // Start with a very low volume
    gainNode.gain.exponentialRampToValueAtTime(volume, context.currentTime + 0.01); // Fade in quickly
    gainNode.gain.exponentialRampToValueAtTime(0.001, context.currentTime + duration); // Fade out

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + duration);
};

export const playCorrectSound = () => {
    // A pleasant, higher-pitched tone for success (C5)
    playTone(523.25, 0.1, 0.3);
};

export const playWrongSound = () => {
    // A lower, dissonant tone for an error (A2)
    playTone(110.00, 0.1, 0.3);
};

export const playPopSound = () => {
    playTone(800, 0.05, 0.2);
};

export const playVroomSound = () => {
    const context = getAudioContext();
    if (!context) return;
    if (context.state === 'suspended') {
        context.resume().catch(console.error);
    }
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
    
    const startTime = context.currentTime;
    oscillator.type = 'sawtooth';
    gainNode.gain.setValueAtTime(0.2, startTime);

    // Vroom effect: quick pitch slide up
    oscillator.frequency.setValueAtTime(80, startTime);
    oscillator.frequency.exponentialRampToValueAtTime(400, startTime + 0.2);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.25);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    oscillator.start(startTime);
    oscillator.stop(startTime + 0.3);
};

export const playEngineIdleSound = (): { oscillator: OscillatorNode, gainNode: GainNode } | null => {
    const context = getAudioContext();
    if (!context) return null;
    if (context.state === 'suspended') {
        context.resume().catch(console.error);
    }

    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(60, context.currentTime); // Low frequency for idle
    gainNode.gain.setValueAtTime(0.05, context.currentTime); // Low volume

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    // FIX: The 'loop' property does not exist on OscillatorNode. Oscillators are continuous by default and will play until stop() is called.
    
    return { oscillator, gainNode };
};

export const playMissSound = () => {
    playTone(150, 0.15, 0.2);
};

export const playSuccessSound = () => {
    const context = getAudioContext();
    if (!context) return;
    playTone(523.25, 0.1, 0.2); // C5
    setTimeout(() => playTone(659.25, 0.1, 0.2), 100); // E5
};

// C major pentatonic across two octaves -- any sequence of these sounds musical.
const PENTATONIC = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25, 783.99, 880.0];
export const playNote = (index: number) => {
    playTone(PENTATONIC[((index % PENTATONIC.length) + PENTATONIC.length) % PENTATONIC.length], 0.22, 0.25);
};

// A soft, piano-like note for playing melodies.
export const playMusicNote = (frequency: number, duration: number, volume = 0.3) => {
    const context = getAudioContext();
    if (!context) return;
    if (context.state === 'suspended') {
        context.resume().catch(console.error);
    }
    const t = context.currentTime;
    const end = t + Math.min(1.2, duration) + 0.15;
    const gainNode = context.createGain();
    gainNode.gain.setValueAtTime(0.0001, t);
    gainNode.gain.exponentialRampToValueAtTime(volume, t + 0.012);
    gainNode.gain.exponentialRampToValueAtTime(volume * 0.4, t + 0.15);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, end);
    gainNode.connect(context.destination);

    const body = context.createOscillator();
    body.type = 'triangle';
    body.frequency.setValueAtTime(frequency, t);
    body.connect(gainNode);

    // A quiet octave overtone adds a little sparkle.
    const overtone = context.createOscillator();
    const overtoneGain = context.createGain();
    overtone.type = 'sine';
    overtone.frequency.setValueAtTime(frequency * 2, t);
    overtoneGain.gain.setValueAtTime(0.25, t);
    overtone.connect(overtoneGain);
    overtoneGain.connect(gainNode);

    body.start(t);
    overtone.start(t);
    body.stop(end + 0.05);
    overtone.stop(end + 0.05);
};

export const playKeyPressSound = () => {
    // A quick, neutral tone for typing
    playTone(1046.50, 0.05, 0.1); // C6
};

export const playGameOverSound = () => {
    const context = getAudioContext();
    if (!context) return;
    playTone(300, 0.1, 0.3); // Low tone
    setTimeout(() => playTone(200, 0.1, 0.3), 120); // Lower
    setTimeout(() => playTone(100, 0.2, 0.3), 240); // Lowest
};

export const playTurkeySound = () => {
    const context = getAudioContext();
    if (!context) return;
    if (context.state === 'suspended') {
        context.resume().catch(console.error);
    }
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
    const now = context.currentTime;

    oscillator.type = 'square';
    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    gainNode.gain.setValueAtTime(0.3, now);

    const baseFreq = 120;
    oscillator.frequency.setValueAtTime(baseFreq, now);

    for (let i = 0; i < 15; i++) {
        const time = now + i * 0.04;
        oscillator.frequency.setValueAtTime(baseFreq + (Math.random() * 60), time);
        oscillator.frequency.setValueAtTime(baseFreq, time + 0.02);
    }
    
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

    oscillator.start(now);
    oscillator.stop(now + 0.6);
};